// Copy / cut / paste of beats (§5.3 Mod+C/X/V).

import type { Draft } from 'immer';
import type { Beat, Score } from '../model/types';
import { reId, emptyTrackBar } from '../model/factories';
import { voiceOf, timeSigAt } from '../model/query';
import { appendBarDraft } from './navigation';
import { type EditorState, mutate, selectionRange, selectedRefs, draftVoice, refingerBeat, focusBeat, hasRange } from './state';

export interface ClipboardData {
  kind: 'simplenotation/beats';
  /** True when whole bars were copied (paste replaces whole bars). */
  wholeBars: boolean;
  /** Copied beats grouped per bar. */
  bars: Beat[][];
  /** Tuning of the source track, to decide whether tab positions can be kept. */
  tuning?: number[];
  capo?: number;
}

export function copySelection(st: EditorState): ClipboardData | null {
  const refs = selectedRefs(st).filter((r) => r.track === st.cursor.track);
  if (!refs.length) return null;
  const r = selectionRange(st);
  const bars: Beat[][] = [];
  let lastBar = -1;
  for (const ref of refs) {
    const b = voiceOf(st.score, ref)[ref.beat];
    if (!b) continue;
    if (ref.bar !== lastBar) {
      bars.push([]);
      lastBar = ref.bar;
    }
    bars[bars.length - 1].push(JSON.parse(JSON.stringify(b)));
  }
  const lastLen = voiceOf(st.score, { ...st.cursor, bar: r.end.bar }).length;
  const wholeBars = hasRange(st) && r.start.beat === 0 && r.end.beat >= lastLen - 1;
  const track = st.score.tracks[st.cursor.track];
  return { kind: 'simplenotation/beats', wholeBars, bars, tuning: track.tuning, capo: track.capo };
}

export function cutSelection(st: EditorState): { state: EditorState; data: ClipboardData | null } {
  const data = copySelection(st);
  if (!data) return { state: st, data };
  const refs = selectedRefs(st).filter((r) => r.track === st.cursor.track);
  const state = mutate(st, (d) => {
    for (const ref of refs) {
      const b = d.tracks[ref.track].bars[ref.bar].voices[ref.voice][ref.beat];
      if (b) {
        b.rest = true;
        b.notes = [];
      }
    }
  });
  return { state, data };
}

export function isClipboardData(x: unknown): x is ClipboardData {
  return !!x && typeof x === 'object' && (x as ClipboardData).kind === 'simplenotation/beats' && Array.isArray((x as ClipboardData).bars);
}

export function paste(st: EditorState, data: ClipboardData): EditorState {
  const c = st.cursor;
  const track = st.score.tracks[c.track];
  const sameTab =
    !!track.tuning &&
    !!data.tuning &&
    track.tuning.length === data.tuning.length &&
    track.tuning.every((n, i) => n === data.tuning![i]) &&
    (track.capo ?? 0) === (data.capo ?? 0);
  const touched: { bar: number; beat: number }[] = [];
  const next = mutate(st, (d) => {
    const prep = (b: Beat): Beat => {
      const x = reId(JSON.parse(JSON.stringify(b)) as Beat);
      if (!sameTab) {
        for (const n of x.notes) {
          delete n.string;
          delete n.fret;
        }
      }
      return x;
    };
    if (data.wholeBars) {
      data.bars.forEach((beats, i) => {
        const bar = c.bar + i;
        while (bar >= d.bars.length) appendBarDraft(d as Score);
        const v = draftVoice(d, { track: c.track, bar, voice: c.voice });
        v.splice(0, v.length, ...(beats.map(prep) as Draft<Beat>[]));
        if (c.voice > 0 && d.tracks[c.track].bars[bar].voices[0].length === 0) {
          d.tracks[c.track].bars[bar].voices[0] = emptyTrackBar(timeSigAt(d as Score, bar)).voices[0] as Draft<Beat>[];
        }
        beats.forEach((_, k) => touched.push({ bar, beat: k }));
      });
    } else {
      const flat = data.bars.flat().map(prep);
      const v = draftVoice(d, c);
      flat.forEach((b, i) => {
        const at = c.beat + i;
        if (at < v.length) v[at] = b as Draft<Beat>;
        else v.push(b as Draft<Beat>);
        touched.push({ bar: c.bar, beat: at });
      });
    }
    if (track.tuning && !sameTab) {
      for (const t of touched) refingerBeat(d, { track: c.track, voice: c.voice, ...t });
    }
  });
  const last = touched[touched.length - 1];
  return last ? focusBeat(next, { ...c, bar: last.bar, beat: last.beat }) : next;
}
